import { api } from './api'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; data?: unknown }
  if (!b?.success) {
    throw new Error((b as { message?: string }).message || 'Request failed')
  }
  return b.data
}

function apiError(err: unknown) {
  const e = err as {
    response?: { data?: { message?: string; error?: { code?: string } }; status?: number }
    message?: string
  }
  const msg = e.response?.data?.message || e.message || 'Request failed'
  const next = new Error(msg)
  ;(next as Error & { code?: string; status?: number }).code = e.response?.data?.error?.code
  ;(next as Error & { code?: string; status?: number }).status = e.response?.status
  return next
}

export type WebhookIngestLogRow = {
  id: string
  provider: string
  providerResourceId: string | null
  orderId: number | null
  storeId: number | null
  orderPaymentMethod: string | null
  outcome: string
  detailMessage: string | null
  errorCode: string | null
  signaturePresent: boolean
  contentType: string | null
  bodyLength: number | null
  bodyPreview: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
}

export type ListWebhookIngestLogsParams = {
  page?: number
  limit?: number
  from?: string
  to?: string
  provider?: 'mollie' | 'paystack'
  orderPaymentMethod?: 'online' | 'pay_on_delivery'
  outcome?: string
}

/** Platform admin — GET /admin/stores/:storeId/webhook-ingest-logs (super_admin JWT). */
export async function fetchWebhookIngestLogsForStore(
  storeId: number,
  params: ListWebhookIngestLogsParams = {},
): Promise<{
  items: WebhookIngestLogRow[]
  total: number
  page: number
  limit: number
}> {
  try {
    const { data } = await api.get(`/admin/stores/${storeId}/webhook-ingest-logs`, {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 25,
        ...(params.from ? { from: params.from } : {}),
        ...(params.to ? { to: params.to } : {}),
        ...(params.provider ? { provider: params.provider } : {}),
        ...(params.orderPaymentMethod ? { orderPaymentMethod: params.orderPaymentMethod } : {}),
        ...(params.outcome?.trim() ? { outcome: params.outcome.trim() } : {}),
      },
    })
    return assertSuccess(data) as {
      items: WebhookIngestLogRow[]
      total: number
      page: number
      limit: number
    }
  } catch (e) {
    throw apiError(e)
  }
}

/** @deprecated Use {@link fetchWebhookIngestLogsForStore} — kept for call-site migration. */
export async function fetchWebhookIngestLogsForOrg(
  _organisationId: number,
  storeId: number,
  params: ListWebhookIngestLogsParams = {},
) {
  return fetchWebhookIngestLogsForStore(storeId, params)
}
